/**
 * Product Features Interactive Functionality
 * Handles tab switching and navigation for product features
 */
document.addEventListener('DOMContentLoaded', function() {
  const productFeatures = document.querySelector('.product-features');
  if (!productFeatures) return;

  const features = productFeatures.querySelectorAll('.product-features__feature');
  const mediaItems = productFeatures.querySelectorAll('.product-features__media-item');
  const prevButton = productFeatures.querySelector('[data-feature-prev]');
  const nextButton = productFeatures.querySelector('[data-feature-next]');
  
  // Constants
  const FALLBACK_HEIGHT = 200;
  
  let activeIndex = 0;

  // Helper function to calculate scroll height
  function calculateScrollHeight(element, isActive) {
    const originalMaxHeight = element.style.maxHeight;
    const originalOpacity = element.style.opacity;
    const originalOverflow = element.style.overflow;
    const originalHeight = element.style.height;
    const originalPosition = element.style.position;
    const originalVisibility = element.style.visibility;
    const originalWidth = element.style.width;
    
    // Get computed width to preserve it during measurement
    const computedWidth = window.getComputedStyle(element).width;
    
    // Temporarily make element visible and measure its natural height
    element.style.maxHeight = 'none';
    element.style.height = 'auto';
    element.style.opacity = '1';
    element.style.overflow = 'visible';
    element.style.visibility = 'hidden';
    element.style.position = 'absolute';
    element.style.width = computedWidth;
    element.style.top = '0';
    element.style.left = '0';
    
    // Force a reflow to ensure styles are applied
    void element.offsetHeight;
    
    const calculatedHeight = element.scrollHeight;
    
    // Restore original styles
    element.style.maxHeight = originalMaxHeight;
    element.style.height = originalHeight;
    element.style.opacity = originalOpacity;
    element.style.overflow = originalOverflow;
    element.style.visibility = originalVisibility;
    element.style.position = originalPosition;
    element.style.width = originalWidth;
    element.style.top = '';
    element.style.left = '';
    
    if (isActive) {
      element.style.maxHeight = calculatedHeight + 'px';
    } else {
      element.style.maxHeight = '0px';
    }
    
    element.dataset.calculatedHeight = calculatedHeight;
    return calculatedHeight;
  }

  function setActiveFeature(index) {
    const isDesktop = window.matchMedia('(min-width: 64rem)').matches;

    // Remove active classes
    features.forEach(feature => {
      feature.classList.remove('product-features__feature--active');
      const title = feature.querySelector('.product-features__title');
      const description = feature.querySelector('.product-features__description');
      
      if (title) {
        title.classList.remove('product-features__title--active');
        title.setAttribute('aria-expanded', 'false');
      }
      if (description) {
        description.classList.remove('product-features__description--active');
        // On mobile, clear description max-height (feature container controls visibility)
        // On desktop, set to 0px to collapse
        if (isDesktop) {
          description.style.maxHeight = '0px';
        } else {
          description.style.maxHeight = '';
        }
      }

      // Only collapse features on mobile
      if (!isDesktop) {
        feature.style.maxHeight = '0px';
      } else {
        // On desktop, clear feature max-height (all features are visible)
        feature.style.maxHeight = '';
      }
    });

    // Remove active classes from all media items
    mediaItems.forEach(item => {
      item.classList.remove('product-features__media-item--active');
    });

    // Add active classes to current feature
    if (features[index]) {
      const activeFeature = features[index];
      const activeTitle = activeFeature.querySelector('.product-features__title');
      const activeDescription = activeFeature.querySelector('.product-features__description');
      
      activeFeature.classList.add('product-features__feature--active');
      if (activeTitle) {
        activeTitle.classList.add('product-features__title--active');
        activeTitle.setAttribute('aria-expanded', 'true');
      }
      if (activeDescription) {
        activeDescription.classList.add('product-features__description--active');
        
        if (isDesktop) {
          // On desktop, use pre-calculated description height for smooth transition
          const descriptionHeight = activeDescription.dataset.calculatedHeight || FALLBACK_HEIGHT;
          activeDescription.style.maxHeight = descriptionHeight + 'px';
        } else {
          // On mobile, clear description max-height (feature container controls visibility)
          activeDescription.style.maxHeight = '';
        }
      }

      // Only set feature max-height on mobile
      if (!isDesktop) {
        const calculatedHeight = activeFeature.dataset.calculatedHeight || FALLBACK_HEIGHT;
        activeFeature.style.maxHeight = calculatedHeight + 'px';
      } else {
        // On desktop, clear feature max-height (features are always visible)
        activeFeature.style.maxHeight = '';
      }
    }

    // Add active class to current media item
    if (mediaItems[index]) {
      mediaItems[index].classList.add('product-features__media-item--active');
    }

    activeIndex = index;
    
    // Announce feature change to screen readers
    const announcement = productFeatures.querySelector('[aria-live]');
    if (announcement && features[index]) {
      const activeTitle = features[index].querySelector('.product-features__title');
      if (activeTitle) {
        announcement.textContent = `Now showing: ${activeTitle.textContent}`;
      }
    }
  }

  function goToPrevious() {
    const newIndex = activeIndex === 0 ? features.length - 1 : activeIndex - 1;
    setActiveFeature(newIndex);
  }

  function goToNext() {
    const newIndex = activeIndex === features.length - 1 ? 0 : activeIndex + 1;
    setActiveFeature(newIndex);
  }

  // Add click handlers to feature titles
  features.forEach((feature, index) => {
    const title = feature.querySelector('.product-features__title');
    if (title) {
      title.addEventListener('click', () => setActiveFeature(index));
    }
  });

  // Add click handlers to navigation buttons
  if (prevButton) {
    prevButton.addEventListener('click', goToPrevious);
  }

  if (nextButton) {
    nextButton.addEventListener('click', goToNext);
  }

  // Keyboard navigation
  productFeatures.addEventListener('keydown', function(/** @type {KeyboardEvent} */ e) {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goToPrevious();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      goToNext();
    }
  });

  // Make the component focusable for keyboard navigation
  productFeatures.setAttribute('tabindex', '0');

  // Calculate and set initial heights for all features and descriptions
  function calculateAllHeights() {
    const isDesktop = window.matchMedia('(min-width: 64rem)').matches;

    features.forEach((feature) => {
      const description = feature.querySelector('.product-features__description');
      
      // Calculate feature height (for mobile)
      if (!isDesktop) {
        calculateScrollHeight(feature, feature.classList.contains('product-features__feature--active'));
      }

      // Calculate description height (for desktop)
      if (description) {
        calculateScrollHeight(description, description.classList.contains('product-features__description--active'));
      }
    });
  }

  // Calculate heights on load
  calculateAllHeights();

  // Ensure initial active feature has max-height set
  const initialActiveFeature = Array.from(features).findIndex(feature => 
    feature.classList.contains('product-features__feature--active')
  );
  if (initialActiveFeature !== -1) {
    activeIndex = initialActiveFeature;
    setActiveFeature(initialActiveFeature);
  }

  // Recalculate on window resize and orientation change
  let resizeTimeout;
  let previousIsDesktop = window.matchMedia('(min-width: 64rem)').matches;
  
  const handleResize = function() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(function() {
      const currentIsDesktop = window.matchMedia('(min-width: 64rem)').matches;
      const viewportChanged = currentIsDesktop !== previousIsDesktop;
      
      if (viewportChanged) {
        previousIsDesktop = currentIsDesktop;
      }
      
      // Use requestAnimationFrame to ensure browser has finished layout
      requestAnimationFrame(function() {
        // Recalculate all heights
        calculateAllHeights();
        
        // Use another requestAnimationFrame to ensure heights are calculated before applying
        requestAnimationFrame(function() {
          // Use setActiveFeature to properly update the active feature with new heights
          if (features[activeIndex]) {
            // Call setActiveFeature which will use the newly calculated heights
            setActiveFeature(activeIndex);
          }
        });
      });
    }, 150);
  };

  window.addEventListener('resize', handleResize);
  window.addEventListener('orientationchange', function() {
    // For orientation change, wait a bit longer for viewport to stabilize
    setTimeout(handleResize, 200);
  });
});
